/**
 * Post suggestion system: 3-stage guided composer.
 *
 * Stage 1: Stance — which side of the debate?
 * Stage 2: Argument — which specific angle?
 * Stage 3: Tone — how to express it?
 *
 * The final suggestion is assembled from stance + argument + tone.
 * Each combination has a pre-written post that discusses the TOPIC ITSELF
 * (never presumes personal experience the user doesn't have).
 *
 * Keyed by topic name.
 */

const POST_SUGGESTION_CONFIG = {
  "AI Exam Monitoring Regulation": {
    stances: {
      pro: {
        label: "I'm concerned about AI monitoring",
        description: "I want to argue for stricter regulation",
        arguments: [
          {
            id: "privacy",
            label: "Privacy concerns",
            description: "Monitoring students in their homes crosses a line",
            icon: "\ud83d\udcf7",
          },
          {
            id: "bias",
            label: "Algorithmic bias",
            description: "The software discriminates against certain students",
            icon: "\u26a0\ufe0f",
          },
          {
            id: "trust",
            label: "Erosion of trust",
            description: "Surveillance undermines the student-university relationship",
            icon: "\ud83e\udd1d",
          },
          {
            id: "stress",
            label: "Mental health impact",
            description: "Being watched by AI creates unnecessary anxiety",
            icon: "\ud83e\udde0",
          },
        ],
      },
      contra: {
        label: "I support AI monitoring",
        description: "I want to argue against stricter regulation",
        arguments: [
          {
            id: "cheating",
            label: "Cheating is the real problem",
            description: "Without monitoring, honest students suffer",
            icon: "\ud83d\udcda",
          },
          {
            id: "degree_value",
            label: "Protecting degree value",
            description: "Unproctored exams devalue everyone's credentials",
            icon: "\ud83c\udf93",
          },
          {
            id: "fairness",
            label: "Fairness for honest students",
            description: "Proctoring levels the playing field",
            icon: "\u2696\ufe0f",
          },
          {
            id: "scalability",
            label: "Practical necessity",
            description: "There's no realistic alternative at scale",
            icon: "\ud83d\udcca",
          },
        ],
      },
    },
    tones: [
      { id: "passionate", label: "Passionate", icon: "\ud83d\udd25", description: "Strong feelings, urgent tone" },
      { id: "measured", label: "Measured", icon: "\ud83e\uddd0", description: "Calm and reasoned" },
      { id: "questioning", label: "Questioning", icon: "\u2753", description: "Asking tough questions" },
      { id: "personal", label: "Direct", icon: "\ud83d\udcac", description: "Plain-spoken, to the point" },
    ],
    // Pre-written posts: suggestions[stance][argumentId][toneId]
    suggestions: {
      pro: {
        privacy: {
          passionate: "Recording students through their webcams during exams is NOT integrity \u2014 it's surveillance normalized. We should be deeply uncomfortable with universities turning private spaces into exam halls!",
          measured: "There's a real question about proportionality here. Webcam monitoring in students' private spaces goes beyond what exam integrity requires. The means don't justify the end.",
          questioning: "At what point does exam security become surveillance? If the software records students in their bedrooms, who decided that was an acceptable trade-off? And who benefits?",
          personal: "Students shouldn't have to accept being watched in their own homes just to take a test. That's a line universities shouldn't cross, period.",
        },
        bias: {
          passionate: "AI proctoring flags students with disabilities at HIGHER rates \u2014 and somehow this is called an integrity tool?! The bias isn't a bug, it's baked into the system!",
          measured: "The documented disparities in false-positive rates across different student populations raise serious equity concerns. An integrity system that discriminates undermines its own purpose.",
          questioning: "If AI proctoring has higher false-positive rates for students with disabilities, how can universities call this fair? Shouldn't the burden of proof be on the technology?",
          personal: "A system that flags fidgeting, looking away, or background noise as cheating doesn't understand how real people take tests. It mistakes being human for being dishonest.",
        },
        trust: {
          passionate: "Starting every exam with the assumption that students are cheaters tells you everything about what universities really think of the people paying tuition!",
          measured: "There's something fundamentally counterproductive about building an educational relationship on surveillance. Trust is a prerequisite for learning, not a luxury to be traded for control.",
          questioning: "If universities don't trust their own students enough to take exams without AI watching, what does that say about the educational relationship? Is this really about integrity?",
          personal: "Education is supposed to be built on trust. Mandatory AI surveillance sends the opposite message: you're guilty until the algorithm says otherwise.",
        },
        stress: {
          passionate: "Students are reporting PANIC during proctored exams \u2014 not because of the questions, but because of the software watching their every move! How is that measuring knowledge?!",
          measured: "Research shows that awareness of being algorithmically monitored increases test anxiety. If the monitoring tool itself affects performance, are we still measuring what we think we're measuring?",
          questioning: "How much of proctored exam performance reflects actual knowledge versus the ability to stay calm while an algorithm analyzes your eye movements? Is anyone even asking this question?",
          personal: "An exam should test what you know, not how well you handle being watched by a machine. The stress of AI proctoring is a confounding variable nobody wants to talk about.",
        },
      },
      contra: {
        cheating: {
          passionate: "While everyone debates privacy, cheaters are making honest students' hard work worthless! Unproctored exams are an open invitation to fraud \u2014 someone needs to say it!",
          measured: "The evidence on cheating rates in unproctored online exams is clear. Without some form of verification, we're essentially running an honor system in an environment where the incentives to cheat are high.",
          questioning: "If half the class can Google answers during an unproctored exam, what exactly is the degree certifying? Shouldn't we ask why opposing monitoring benefits the people least committed to earning their grades?",
          personal: "Students who study honestly deserve to know their grades mean something. Without proctoring, there's nothing stopping cheaters from devaluing everyone else's work.",
        },
        degree_value: {
          passionate: "A degree that anyone can cheat their way through is WORTHLESS! If we don't protect the credential with proctoring, we're handing employers a reason to trust none of us!",
          measured: "The value of a credential depends on the integrity of the assessment process. When unproctored exam scores are systematically inflated, it's the honest students whose degrees lose value first.",
          questioning: "What happens to the value of a degree when employers can't trust that graduates actually passed their exams honestly? Isn't that a bigger threat to students than a monitored test?",
          personal: "Years of genuine effort should result in a credential that means something. Without proctoring, every degree carries an asterisk that nobody earned.",
        },
        fairness: {
          passionate: "The anti-proctoring crowd wants to abolish the ONLY tool that levels the playing field! How is it fair that honest students compete against people with answer keys open on a second screen?!",
          measured: "There's an equity argument for proctoring that often gets overlooked: students who lack the resources or networks to cheat effectively are disproportionately disadvantaged when monitoring is absent.",
          questioning: "Everyone talks about the rights of monitored students. But what about the rights of honest students who compete against cheaters? Where's the fairness in an unproctored system?",
          personal: "Proctoring isn't pleasant, but it's the only thing ensuring that grades reflect actual learning. Take it away and you're rewarding dishonesty at everyone else's expense.",
        },
        scalability: {
          passionate: "In-person proctoring for thousands of remote students? That's a FANTASY! The people opposing AI proctoring have no realistic alternative \u2014 they just want the problem to disappear!",
          measured: "The practical reality is that traditional proctoring cannot scale to remote education. Opposing AI proctoring without proposing a viable alternative isn't a policy position \u2014 it's wishful thinking.",
          questioning: "What's the actual alternative to AI proctoring for thousands of remote students? In-person monitoring? The cost alone makes it impossible. So what's the real proposal here?",
          personal: "Remote education needs remote proctoring. The technology isn't perfect, but the alternative \u2014 no monitoring at all \u2014 isn't a solution. It's giving up.",
        },
      },
    },
  },

  "Welfare Reform": {
    stances: {
      pro: {
        label: "I support the reform",
        description: "I want to argue for tighter conditionality and accountability",
        arguments: [
          {
            id: "fairness",
            label: "Fairness to working people",
            description: "Work should always pay more than welfare",
            icon: "\u2696\ufe0f",
          },
          {
            id: "reciprocity",
            label: "Reciprocity",
            description: "Society can expect contribution from those it supports",
            icon: "\ud83e\udd1d",
          },
          {
            id: "accountability",
            label: "Accountability of public money",
            description: "State money should fund essentials, not waste",
            icon: "\ud83d\udcb3",
          },
          {
            id: "fiscal",
            label: "Fiscal sustainability",
            description: "Unconditional generosity isn't affordable forever",
            icon: "\ud83d\udcca",
          },
        ],
      },
      contra: {
        label: "I oppose the reform",
        description: "I want to argue against the punitive measures",
        arguments: [
          {
            id: "dignity",
            label: "Dignity and stigma",
            description: "Cards and sanctions create second-class citizenship",
            icon: "\ud83d\udc94",
          },
          {
            id: "effectiveness",
            label: "Sanctions don't work",
            description: "Punitive measures fail to move people into employment",
            icon: "\ud83d\udeab",
          },
          {
            id: "scapegoating",
            label: "The 'refuser' is a myth",
            description: "Reform targets a problem that barely exists",
            icon: "\ud83c\udfad",
          },
          {
            id: "vulnerability",
            label: "Vulnerable recipients",
            description: "Most affected are families and people with health conditions",
            icon: "\ud83e\udec2",
          },
        ],
      },
    },
    tones: [
      { id: "passionate", label: "Passionate", icon: "\ud83d\udd25", description: "Strong feelings, urgent tone" },
      { id: "measured", label: "Measured", icon: "\ud83e\uddd0", description: "Calm and reasoned" },
      { id: "questioning", label: "Questioning", icon: "\u2753", description: "Asking tough questions" },
      { id: "personal", label: "Direct", icon: "\ud83d\udcac", description: "Plain-spoken, to the point" },
    ],
    suggestions: {
      pro: {
        fairness: {
          passionate: "When a person working forty hours a week takes home LESS than someone refusing every job offer, the system is broken! Restoring that gap isn't punishment. It's basic fairness to people who show up!",
          measured: "The principle that work should always pay more than welfare is fundamental to a functioning labour market. Years of unconditional generosity have quietly eroded that gap. Restoring it through targeted reform is overdue.",
          questioning: "If welfare can match or exceed full-time wages, what message does that send to people working long hours at modest pay? Who exactly is the system meant to reward?",
          personal: "Work should always pay more than welfare. That principle has slipped in recent years, and the reform restores it. Nothing radical about that.",
        },
        reciprocity: {
          passionate: "Solidarity isn't a one-way ATM! When you accept support from society, society has the right to expect SOMETHING back. Otherwise it's not solidarity, it's extraction!",
          measured: "A welfare system functions on mutual obligation. The state extends support to those in need, and recipients commit to genuine effort to return to work. That reciprocal logic has been quietly abandoned. The reform reasserts it.",
          questioning: "What is solidarity if it asks nothing of those who receive it? At what point does generosity without expectation simply become a permanent transfer from working people to non-working people?",
          personal: "The deal has always been: society helps you when you're down, you work hard to climb back up. Both sides matter. The reform just restores the second half of that bargain.",
        },
        accountability: {
          passionate: "Cash benefits funded by taxpayer money disappearing into alcohol, gambling, and tobacco is exactly the kind of waste that destroys public trust in welfare itself! The card protects the system from its critics!",
          measured: "Controlled payment systems already exist in many social-support programs. Restricting benefit use to essentials is not a moral judgement of recipients. It is appropriate stewardship of public money, and it preserves political support for the system.",
          questioning: "Why is it controversial to ensure that public funds are spent on the things the public is paying for? Pension contributions can't be cashed out for cigarettes. Why should benefits be different?",
          personal: "State money should fund essentials, not luxuries. The payment card is a practical way to ensure that. Most reasonable people accept the same logic in every other government program.",
        },
        fiscal: {
          passionate: "Working people are being squeezed by every kind of cost increase while welfare budgets keep climbing without limits! Something has to give. Asking recipients to participate in the system that supports them isn't cruel. It's mathematics!",
          measured: "Welfare spending has grown faster than tax revenue for over a decade. Without reform, the system becomes either unaffordable or unfair to those who fund it. The current reform restores fiscal sustainability before the politics turn ugly.",
          questioning: "If welfare spending continues to grow without conditions, who pays for it? At what level of contribution will working taxpayers say: enough? Better to reform now than to lose public support entirely.",
          personal: "Welfare costs money. That money comes from people who work. If the system isn't sustainable, it doesn't survive. The reform tries to keep it sustainable. That seems worth doing.",
        },
      },
      contra: {
        dignity: {
          passionate: "A card that tells everyone in the supermarket you're on welfare. A sanction that cuts off your support for months. This isn't policy, it's HUMILIATION dressed up as accountability!",
          measured: "Restricted payment cards create a visible marker of welfare status at every transaction. Sanctions that suspend payments push families closer to destitution. The reform treats recipients as a group to be controlled rather than supported. That has consequences.",
          questioning: "What kind of society marks its poorest citizens at every checkout? If we wouldn't accept these conditions for any other group, why is it acceptable for people relying on welfare?",
          personal: "The payment card and the sanctions framework strip dignity from people who already have very little. There are better ways to ensure accountability than humiliation.",
        },
        effectiveness: {
          passionate: "Sanctions don't conjure jobs out of thin air! Cutting off someone's benefits doesn't create employers, it creates homelessness! The whole approach treats unemployment as a moral failing instead of a labour-market problem!",
          measured: "Empirical research on sanction regimes consistently finds limited impact on employment outcomes. People driven off benefits often disappear from official data rather than entering work. The reform addresses appearances rather than the underlying labour-market dynamics.",
          questioning: "If sanctions actually moved people into stable employment, we would see it in the data. Why do studies repeatedly find that punitive welfare reform pushes people out of the system without finding them jobs? Is the goal employment, or is it something else?",
          personal: "Cutting benefits doesn't create jobs. It just makes life harder for people already struggling to find one. If the reform were really about employment, it would invest in placement, not punishment.",
        },
        scapegoating: {
          passionate: "The whole reform is built around an INVISIBLE villain that barely exists! Researchers can't find significant numbers of 'work refusers'. The political class needs a scapegoat to sell this thing, so they invented one!",
          measured: "Studies of welfare recipients consistently find that the 'work refuser' figure is statistically marginal. The vast majority of recipients are parents, people with health conditions, and workers between jobs. Reforming an entire system around a rhetorical fiction is poor policy design.",
          questioning: "Where exactly is this army of work refusers? Every empirical study finds the actual numbers are tiny. So why is the reform being sold as a response to a problem that researchers can barely measure?",
          personal: "The 'lazy refuser' is a political invention. Real welfare recipients are people having a hard time. The reform punishes the second group to score points against the first.",
        },
        vulnerability: {
          passionate: "The reform falls hardest on people who already have the least! Sanctions on a mother of two. Payment cards for someone managing a chronic illness. This isn't fiscal discipline. It's CRUELTY pretending to be policy!",
          measured: "The majority of welfare recipients are not the figures evoked in the reform's rhetoric. They are families with children, people with disabilities, and workers in precarious employment. Subjecting these groups to sanctions and restricted spending creates real hardship for solving a phantom problem.",
          questioning: "If most welfare recipients are families and people with health conditions, who exactly does the reform target? And how does making their lives harder benefit anyone, including the taxpayers funding the system?",
          personal: "Welfare in our country is mostly going to people who are doing the best they can: parents, people in poor health, workers between jobs. The reform makes life worse for them. That's the actual outcome, regardless of the slogans.",
        },
      },
    },
  },

  "Immigration Policy in Germany": {
    stances: {
      pro: {
        label: "I support stricter immigration controls",
        description: "I want to argue for tighter enforcement and limits",
        arguments: [
          {
            id: "security",
            label: "Public safety",
            description: "Security concerns after incidents like Solingen",
            icon: "\ud83d\udee1\ufe0f",
          },
          {
            id: "capacity",
            label: "Municipalities overwhelmed",
            description: "Cities and towns have reached their limits",
            icon: "\ud83c\udfe2",
          },
          {
            id: "rule_of_law",
            label: "Enforce existing rules",
            description: "Deportation orders must actually be carried out",
            icon: "\u2696\ufe0f",
          },
          {
            id: "integration",
            label: "Integration needs time",
            description: "Too many arrivals too fast prevents successful integration",
            icon: "\ud83e\uddf3",
          },
        ],
      },
      contra: {
        label: "I support humanitarian immigration",
        description: "I want to argue for open, rights-based immigration policy",
        arguments: [
          {
            id: "human_rights",
            label: "Constitutional asylum right",
            description: "Human dignity and the right to asylum are non-negotiable",
            icon: "\ud83d\udcdc",
          },
          {
            id: "demographics",
            label: "Demographic necessity",
            description: "Germany needs immigration to sustain an aging society",
            icon: "\ud83d\udcc9",
          },
          {
            id: "integration_works",
            label: "Integration works",
            description: "Millions have successfully integrated over decades",
            icon: "\u2705",
          },
          {
            id: "solidarity",
            label: "Historical responsibility",
            description: "European values and solidarity demand openness",
            icon: "\ud83e\udd1d",
          },
        ],
      },
    },
    tones: [
      { id: "passionate", label: "Passionate", icon: "\ud83d\udd25", description: "Strong feelings, urgent tone" },
      { id: "measured", label: "Measured", icon: "\ud83e\uddd0", description: "Calm and reasoned" },
      { id: "questioning", label: "Questioning", icon: "\u2753", description: "Asking tough questions" },
      { id: "personal", label: "Direct", icon: "\ud83d\udcac", description: "Plain-spoken, to the point" },
    ],
    suggestions: {
      pro: {
        security: {
          passionate: "After Solingen, anyone still saying we don't have a security problem is living in denial! People were MURDERED because someone who should have been deported was still here!",
          measured: "The Solingen attack exposed systemic failures in enforcement. When individuals with rejected asylum claims remain in the country and commit violent crimes, public trust in the system erodes \u2014 justifiably so.",
          questioning: "How many security incidents need to happen before we admit that gaps in deportation enforcement create real risks? Is it wrong to expect the state to protect its citizens?",
          personal: "People have a right to feel safe. When the state fails to remove individuals who have no legal right to stay and who pose a threat, it fails in its most basic duty.",
        },
        capacity: {
          passionate: "Schools overcrowded, housing impossible to find, social workers burnt out \u2014 and Berlin says municipalities should just COPE?! Local communities are at breaking point!",
          measured: "Municipal capacity is not unlimited. When cities lack housing, school places, and administrative staff to process arrivals, the quality of reception and integration suffers for everyone involved.",
          questioning: "If mayors across the political spectrum are saying they've reached capacity, why does acknowledging limits get treated as xenophobia? Isn't ignoring local reality the actual irresponsible position?",
          personal: "Cities can't house people they don't have space for. Schools can't teach in classes of 40. There's nothing hateful about saying the system is full \u2014 it's just honest.",
        },
        rule_of_law: {
          passionate: "Over 200,000 people with rejected asylum claims are still in Germany \u2014 and we wonder why nobody trusts immigration policy anymore?! Laws mean NOTHING if they're never enforced!",
          measured: "A legal system that issues deportation orders but systematically fails to execute them undermines its own credibility. Consistent enforcement isn't cruelty \u2014 it's what distinguishes rule of law from arbitrariness.",
          questioning: "What message does it send when deportation orders exist only on paper? If the state won't enforce its own decisions, why should anyone \u2014 citizens or migrants \u2014 take the rules seriously?",
          personal: "Either laws apply or they don't. Keeping deportation orders on the books while never carrying them out is the worst of both worlds: it satisfies no one and solves nothing.",
        },
        integration: {
          passionate: "Integration isn't magic \u2014 it takes time, resources, and manageable numbers! Pretending unlimited arrivals are compatible with successful integration is a LIE that hurts everyone!",
          measured: "Successful integration requires language courses, job placement, and social contact \u2014 all of which are finite resources. When arrivals outpace integration infrastructure, outcomes worsen for newcomers and host communities alike.",
          questioning: "If integration courses have year-long waiting lists and language teachers are impossible to find, how does adding more people to the queue help anyone actually integrate? Where's the plan?",
          personal: "Integration works when there's enough time and support to make it real. Overloading the system doesn't help refugees \u2014 it traps them in limbo and breeds resentment all around.",
        },
      },
      contra: {
        human_rights: {
          passionate: "The right to asylum is in the Grundgesetz for a REASON \u2014 because Germany knows what happens when states turn away the persecuted! Are we really going to repeat history's worst lessons?!",
          measured: "Article 16a exists because of Germany's historical experience. The right to asylum is a constitutional guarantee, not a policy preference to be adjusted based on opinion polls.",
          questioning: "If human dignity is inviolable and the right to asylum is constitutionally guaranteed, on what legal or moral basis do we restrict it? Since when are fundamental rights subject to capacity arguments?",
          personal: "Asylum is a right, not a privilege. People fleeing war and persecution don't stop deserving protection because it's politically inconvenient. The Grundgesetz is clear on this.",
        },
        demographics: {
          passionate: "Germany's workforce is SHRINKING by hundreds of thousands per year \u2014 and the response is to make immigration harder?! Without new workers, pensions collapse and hospitals close. It's that simple!",
          measured: "Germany faces a structural demographic gap: more retirees, fewer workers. Immigration is not the only solution, but no serious projection shows the labor market stabilizing without significant net migration.",
          questioning: "Who will staff the care homes, drive the buses, and pay into the pension system when the baby boomers retire? If not immigrants, then who exactly? Does anyone have an actual answer?",
          personal: "Germany is getting older fast. Every sector is short-staffed. Immigration isn't charity \u2014 it's demographic survival. Restricting it now is cutting off our nose to spite our face.",
        },
        integration_works: {
          passionate: "Millions of people with migration backgrounds are doctors, teachers, business owners, and taxpayers in this country \u2014 and we act like integration is impossible?! Open your eyes to the success stories all around you!",
          measured: "Germany has integrated millions of people over decades \u2014 from Gastarbeiter to refugees of the 1990s. The evidence shows that with time and opportunity, integration succeeds far more often than the current panic suggests.",
          questioning: "If integration has supposedly failed, how do we explain the millions of Germans with migration backgrounds who work, vote, and contribute every day? Is the failure narrative based on data or fear?",
          personal: "Look at any hospital, university, or mid-sized business in Germany. People with migration backgrounds are everywhere, doing essential work. Integration isn't a theory \u2014 it's reality.",
        },
        solidarity: {
          passionate: "Europe was built on the promise of NEVER AGAIN \u2014 never again turning away the persecuted, never again defining who deserves dignity! Fortress Europe betrays everything we claim to stand for!",
          measured: "Germany's postwar identity is built on responsibility and solidarity. Retreating from humanitarian obligations doesn't just affect refugees \u2014 it undermines the moral foundation on which European cooperation rests.",
          questioning: "If the wealthiest continent on earth can't offer protection to people fleeing war, what do European values actually mean? Are solidarity and human rights only for those already inside the walls?",
          personal: "Germany is rich, stable, and at peace. Helping people who have none of those things isn't weakness \u2014 it's what strong societies do. Solidarity isn't a burden, it's a choice about who we are.",
        },
      },
    },
  },
};

/**
 * Get suggestion config for a topic.
 * @param {string} topicName
 * @returns {Object|null}
 */
export function getPostSuggestionConfig(topicName) {
  return POST_SUGGESTION_CONFIG[topicName] || null;
}

export default POST_SUGGESTION_CONFIG;
